import { WhereClauses } from '@/composables/charts/influx-charts/chart-serialization/WhereClauses'
import Highcharts, { Chart } from 'highcharts'

import { IncidentReducer, IncidentReductionSettings } from '@/composables/charts/influx-charts/incident-reducers/IncidentReducers'
import { ChartIncidentAnnotator, SerializedChartAnnotatorType } from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import { CompiledInfluxQueryObject, TagSelector, xAggregations } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { TimeSeriesChartBuilder } from '@/composables/charts/influx-charts/chart-builders/TimeSeriesChartBuilder'
import { CustomPredicates } from '@/composables/charts/influx-charts/chart-serialization/CustomPredicates'
import { Component } from 'vue'
import type DataTransformPlugin from '@/composables/charts/influx-charts/DataTransformPlugin'

import vuetify from '@/plugins/vuetify'
import { useSettingsStore } from '@/store/settings'
import { getCurrentTimezone } from '@/composables/datetime'
import { useMeasurementStore } from '@/store/measurements'
import InfluxQueryResultPipeline from '@/composables/charts/influx-charts/InfluxQueryResultPipeline'
import { BaseSerializedInfluxChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'

export abstract class InfluxChartBuilder extends TimeSeriesChartBuilder {
  whereClauses?: WhereClauses
  transientWhereClauses?: WhereClauses //should not be serialized, these are transient clauses added by subclasses
  customPredicates?: CustomPredicates
  transientCustomPredicates?: CustomPredicates
  incidentReducer?: IncidentReducer
  xAggregation: xAggregations = 'mean'
  incidentAnnotators?: { annotator: ChartIncidentAnnotator; enabled: boolean }[]
  fill: number | 'linear' | 'none' | 'previous' | null | undefined = undefined
  lastAnnotationRenderStart: number = 0
  lastAnnotationRenderEnd: number = 0
  firstBarnDocAnnotationTimestamp?: number
  groupByTags?: string[]
  transientGroupByTags?: string[] = [] //should not be serialized, these are transient tags added by subclasses
  tagSelectors?: TagSelector<string>[]
  transientTagSelectors?: TagSelector<string>[]
  private openAnnotationListeners: Array<(component: Component, props: Record<string, unknown>) => void> = []
  protected readonly dataTransformPlugins: DataTransformPlugin[] = []

  protected constructor(serialized: BaseSerializedInfluxChart) {
    super(serialized)
    if (serialized.whereClauses) this.whereClauses = serialized.whereClauses
    if (serialized.customPredicates) this.customPredicates = serialized.customPredicates
    if (serialized.xAggregation) this.xAggregation = serialized.xAggregation
    this.fill = serialized.fill
    if (serialized.groupByTags) this.groupByTags = serialized.groupByTags
    if (serialized.tagSelectors) this.tagSelectors = serialized.tagSelectors
    if (serialized.incidentReducer) this.incidentReducer = IncidentReducer.fromSerialized(serialized.incidentReducer)
    if (serialized.incidentAnnotators)
      this.incidentAnnotators = serialized.incidentAnnotators.map(serializedAnnotator => ({
        enabled: true,
        annotator: ChartIncidentAnnotator.fromSerialized(serializedAnnotator, this.autoInferChartEntity(serialized)),
      }))
  }

  serialize(): BaseSerializedInfluxChart {
    return {
      ...super.serialize(),
      ...(this.whereClauses && { whereClauses: this.whereClauses }),
      ...(this.incidentReducer && { incidentReducer: this.incidentReducer.serialize() }),
      ...(this.customPredicates && { customPredicates: this.customPredicates }),
      ...(this.groupByTags && { groupByTags: this.groupByTags }),
      ...(this.incidentAnnotators && {
        incidentAnnotators: this.incidentAnnotators.map(({ annotator }) => annotator.serialize() as SerializedChartAnnotatorType),
      }),
      ...(this.tagSelectors && { tagSelectors: this.tagSelectors }),
      ...(this.fill !== undefined && { fill: this.fill }),
      xAggregation: this.xAggregation,
    }
  }

  abstract buildQuery(): CompiledInfluxQueryObject

  protected async loadData() {
    const signal = this.abortController?.signal
    if (signal?.aborted) return
    const query = this.buildQuery()

    let compiledReducerSettings: IncidentReductionSettings | undefined = undefined
    if (this.incidentReducer && (await this.incidentReducer.canReduce())) compiledReducerSettings = await this.incidentReducer.getIncidentReduction()
    if (signal?.aborted) return
    const data = await useMeasurementStore().loadGenericData(query.path, query.query, compiledReducerSettings, signal)
    const annotatorPromises: Promise<void>[] = []
    if (this.incidentAnnotators && !signal?.aborted) {
      for (const { annotator } of this.incidentAnnotators) {
        annotatorPromises.push(annotator.load(this.timeSetup.getComputedStart(), this.timeSetup.getComputedEnd(), signal))
      }
      await Promise.all(annotatorPromises)
    }
    if (signal?.aborted) return
    if (data) {
      const pipeline = new InfluxQueryResultPipeline(this.dataTransformPlugins, query)
      this.series = (await pipeline.prepareData(data)) as Highcharts.SeriesOptionsType[]
      this.buildOptions()
    }
  }

  attachOpenAnnotationListener(listener: (component: Component, props: Record<string, unknown>) => void) {
    this.openAnnotationListeners.push(listener)
  }

  detachOpenAnnotationListener(listener: (component: Component, props: Record<string, unknown>) => void) {
    this.openAnnotationListeners.push(listener)
  }

  notifyOpenAnnotationListeners(component: Component, props: Record<string, unknown>) {
    for (const openAnnotationListener of this.openAnnotationListeners) {
      openAnnotationListener(component, props)
    }
  }

  calculateYExtremes(): { min: number; max: number; dataMin: number; dataMax: number } {
    let globalMin = Number.MAX_VALUE
    let globalMax = Number.MIN_VALUE

    const seriesData: { x: number; y: number | null | undefined }[][] = []

    if (this.series) {
      for (const s of this.series) {
        const data = (s as Highcharts.SeriesOptionsType & { data: { x: number; y: number | null | undefined }[] }).data
        if (data) {
          seriesData.push(data)
          for (const point of data) {
            if (point.y !== null && point.y !== undefined) {
              if (point.y < globalMin) globalMin = point.y
              if (point.y > globalMax) globalMax = point.y
            }
          }
        }
      }
    }

    // If no data found, provide some defaults or use 0-1
    if (globalMin === Number.MAX_VALUE) globalMin = 0
    if (globalMax === Number.MIN_VALUE) globalMax = 1

    return {
      min: globalMin,
      max: globalMax,
      dataMin: globalMin,
      dataMax: globalMax,
    }
  }

  buildAnnotationOptions(
    chart: Chart | undefined,
    series: { x: number; y: number | null | undefined }[][] | undefined = undefined,
    extremes: { min: number; max: number; dataMin: number; dataMax: number } | undefined = undefined
  ): void {
    let start, end
    if (chart) {
      start = chart.xAxis[0].min
      end = chart.xAxis[0].max
    } else {
      const compiledTimeSetup = this.timeSetup.compile()
      start = compiledTimeSetup.start
      end = compiledTimeSetup.end
    }

    if (start !== undefined) this.lastAnnotationRenderStart = start
    if (end !== undefined) this.lastAnnotationRenderEnd = end
    const annotatorOptions: Highcharts.AnnotationsOptions[] = []
    const compiledTimeSetup = this.timeSetup.compile()
    this.incidentAnnotators?.forEach(({ annotator, enabled }) => {
      if (!enabled) return
      annotator.compileAnnotations(
        compiledTimeSetup.start,
        compiledTimeSetup.end,
        chart,
        this.notifyOpenAnnotationListeners.bind(this),
        annotationGroupId => {
          if (!this.chartRef?.value?.chart?.annotations) return
          for (const annotation of this.chartRef?.value?.chart.annotations) {
            if (annotation?.userOptions?.id?.toString().startsWith(annotationGroupId)) {
              annotation.shapes?.forEach((shape: { graphic: Highcharts.SVGElement }) => shape.graphic.attr({ opacity: 1 }))
              annotation.labels?.forEach((label: { graphic: Highcharts.SVGElement }) => label.graphic.attr({ opacity: 1 }))
            } else {
              annotation.shapes?.forEach((shape: { graphic: Highcharts.SVGElement }) => shape.graphic.attr({ opacity: 0.25 }))
              annotation.labels?.forEach((label: { graphic: Highcharts.SVGElement }) => label.graphic.attr({ opacity: 0.25 }))
            }
          }
        },
        () => {
          if (!this.chartRef?.value?.chart?.annotations) return
          for (const annotation of this.chartRef?.value?.chart.annotations) {
            annotation.shapes?.forEach((shape: { graphic: Highcharts.SVGElement }) => shape?.graphic?.attr({ opacity: 1 }))
            annotation.labels?.forEach((label: { graphic: Highcharts.SVGElement }) => label?.graphic?.attr({ opacity: 1 }))
          }
        },
        series,
        extremes
      )
      annotatorOptions.push(...annotator.getAnnotations())
    })

    if (this.firstBarnDocAnnotationTimestamp) {
      annotatorOptions.push({
        draggable: '',
        zIndex: 10,
        shapes: [
          {
            type: 'path',
            stroke: 'red',
            strokeWidth: 4,
            points: [
              { xAxis: 0, yAxis: 0, x: this.firstBarnDocAnnotationTimestamp, y: Number.MIN_SAFE_INTEGER },
              { xAxis: 0, yAxis: 0, x: this.firstBarnDocAnnotationTimestamp, y: Number.MAX_SAFE_INTEGER },
            ],
          },
        ],
      })
    }

    if (!this.options) this.options = {}
    this.options.annotations = annotatorOptions
  }

  shouldReRenderAnnotations(): boolean {
    if (!this.chartRef?.value) return false
    const start = this.chartRef?.value?.chart.xAxis[0].min
    const end = this.chartRef?.value?.chart.xAxis[0].max
    if (start === undefined || end === undefined) return false
    return !(this.lastAnnotationRenderStart === start && this.lastAnnotationRenderEnd === end)
  }

  buildOptions(): void {
    const theme = vuetify.theme.themes.value[useSettingsStore().theme]
    const chartOptions: Highcharts.ChartOptions = {
      events: {
        redraw: () => {
          if (!this.shouldReRenderAnnotations()) return
          if (this.chartRef?.value?.chart?.yAxis?.length < 1) return
          this.buildAnnotationOptions(this.chartRef?.value?.chart)
          this.triggerUpdate()
        },
      },
      type: this.getRenderChartType(),
      style: {
        fontFamily: 'roboto',
      },
      zooming: {
        type: 'xy',
      },
      backgroundColor: 'transparent',
    }
    if (this.seriesBasedHeight && this.series?.length) chartOptions.height = Math.max(40 * this.series.length, 400)
    const noDataOptions: Highcharts.NoDataOptions = {
      style: { fontWeight: 'bold', fontSize: '20px' },
    }
    const loadingOptions: Highcharts.LoadingOptions = {
      style: {
        backgroundColor: 'transparent',
        fontSize: '20px',
      },
      showDuration: 50,
      hideDuration: 500,
    }

    const creditOptions: Highcharts.CreditsOptions = {
      text: 'vetvise.com',
      href: 'https://vetvise.com',
    }
    const timeOptions: Highcharts.TimeOptions = {
      timezone: getCurrentTimezone(),
    }

    const titleOptions: Highcharts.TitleOptions = {
      text: this.title,
      style: { color: theme.colors.onSurface },
    }
    const subtitleOptions: Highcharts.SubtitleOptions = {
      text: this.subtitle,
      style: { color: theme.colors.onSurface },
    }

    const compiledTimeSetup = this._timeSetup.compile()
    const xAxisOptions: Highcharts.XAxisOptions = {
      type: 'datetime',
      min: compiledTimeSetup.start,
      max: compiledTimeSetup.end,
      labels: {
        style: {
          color: theme.colors.onSurface,
        },
      },
    }

    const exportingOptions: Highcharts.ExportingOptions = {
      enabled: true,
      fallbackToExportServer: false,
      sourceWidth: 3000,
      sourceHeight: 500,
    }

    const tooltipOptions: Highcharts.TooltipOptions = {
      distance: 25,
      hideDelay: 100,
      pointFormatter: function () {
        return `<span style='color: ${this.color}'>●</span> ${this.series.name}: <b>${this.y?.toPrecision(5)}</b><br>`
      },
    }

    const legendOptions: Highcharts.LegendOptions = {
      itemStyle: {
        color: theme.colors.onSurface,
      },
    }

    this.options = {
      chart: chartOptions,
      title: titleOptions,
      subtitle: subtitleOptions,
      xAxis: xAxisOptions,
      noData: noDataOptions,
      loading: loadingOptions,
      credits: creditOptions,
      time: timeOptions,
      plotOptions: this.plotOptions,
      exporting: exportingOptions,
      tooltip: tooltipOptions,
      yAxis: this.buildYAxisOptions(),
      legend: legendOptions,
    }

    // Pre-calculate annotations
    const extremes = this.calculateYExtremes()
    const seriesData: { x: number; y: number | null | undefined }[][] = []
    if (this.series) {
      for (const s of this.series) {
        const data = (s as Highcharts.SeriesOptionsType & { data: { x: number; y: number | null | undefined }[] }).data
        if (data) seriesData.push(data)
      }
    }
    this.buildAnnotationOptions(undefined, seriesData, extremes)
    if (this.options.annotations) {
      // Ensure annotations are part of the initial options
    }
  }

  changeAnnotator(annotatorId: string, status: boolean) {
    if (!this.incidentAnnotators) return
    for (const annotator of this.incidentAnnotators) {
      if (annotator.annotator.annotatorId === annotatorId) {
        annotator.enabled = status
        this.buildAnnotationOptions(this.chartRef?.value?.chart)
        this.triggerUpdate()
        return
      }
    }
  }

  startBarnDocAnnotation(firstTimestamp: number) {
    this.firstBarnDocAnnotationTimestamp = firstTimestamp
    if (this.incidentAnnotators)
      for (const incidentAnnotator of this.incidentAnnotators) {
        incidentAnnotator.enabled = false
      }
    this.buildAnnotationOptions(this.chartRef?.value?.chart)
    this.triggerUpdate()
  }

  cancelBarnDocAnnotation(): void {
    this.firstBarnDocAnnotationTimestamp = undefined
    this.buildAnnotationOptions(this.chartRef?.value?.chart)
    this.triggerUpdate()
  }
  onChartCreated(chart: Chart) {
    if (chart.yAxis?.length < 1) return
    this.buildAnnotationOptions(chart)
    this.triggerUpdate()
  }
}
